import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Form, Container, Row, Col } from 'react-bootstrap';
import {
  MoreDetails,
  ActionButtons,
  PatientInfo,
  TagsDropdown,
  PatientInfoDetails,
} from './components';
import { useTags } from '../../hooks/useTags';
import { usePatientProfile } from './hooks';

import ReconciliationSummaryPopup from './ReconciliationSummaryPopup/ReconciliationSummaryPopup';
import DataMapperProfileBar from '../DataMapper/DataMapperProfileBar';
import {
  checkingPatientProfileStatus,
  setPatientProfileState,
} from '../../../store/ai2';
import './ProfileBar.scss';
import { AdditionalSection } from './components/AdditionalSection/AdditionalSection';
import { useAssignPatient } from '../../hooks';
import { MedicationProfileCard } from '../../pages/MedicationProfile';
import { useEffect } from 'react';
import { SystemPermission, usePatientDetail } from '../../../context';
import AdditionalInsightsPopup from './AdditionalInsightsPopup/AdditionalInsightsPopup';
import type { RootState } from '../../../store';
import { LoadingSpinner } from '../LoadingSpinner';
import ExternalProviderButton from './components/ExternalProvider/ExternalProviderButton';
import { ExternalProvider } from '../../../lib';
import MedicationProfilesProvider from './context/MedicationProfilesProvider';
import DeIndentifiedPatientHeader from './DeIndentifiedPatientHeader/DeIndentifiedPatientHeader';
import { mapToEpisodeDetailsKey } from './components/AdditionalSection/utils';

interface ProfileBarProps {
  isLoading: boolean;
}

const ProfileBar: React.FC<ProfileBarProps> = ({ isLoading }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const patientAccessToken = searchParams.get('access_token');
  const { clinics, selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );
  const { rawEvents, latestEpisodeId } = useSelector(
    (state: any) => state.timeline,
  );

  const { patientProfile, patientProfileStatus, features } = useSelector(
    (state: RootState) => state.patientProfile,
  );

  const permissions = selectedTenancy?.userPermissions;
  const hasDeidentifiedPermission =
    !!selectedTenancy?.permissionsSetup &&
    !!permissions?.[SystemPermission.DEIDENTIFIED_DATA];

  const newSectionConfig = useSelector(
    (state: RootState) => state.patientProfile.features?.additionalSection,
  );
  const { setPatient } = usePatientDetail();

  const { setFavourite } = useAssignPatient();
  const { atags, addTag } = useTags(
    ['CTO', 'No appt.', 'tag1'],
    ['tag2', 'tag3', 'tag4', 'tag5', 'tag6', 'tag7'],
  );

  useEffect(() => {
    if (rawEvents?.Medications) {
      setPatient({
        medications: rawEvents.Medications,
        episodes: patientProfile?.clinics?.[0]?.episodes || [],
        latestEpisodeId: latestEpisodeId,
      });
    }
  }, [rawEvents]);

  const config = features?.patientProfileHeaders;
  const {
    setOpenPatientInfo,
    setEditProfile,
    dropdownOpen,
    setDropdownOpen,
    editProfile,
    openPatientInfo,
    openAdditionalInsights,
    openReconciliationSummary,
    openMedicationProfile,
    setOpenReconciliationSummary,
    setOpenAdditionalInsights,
    toggleMedicationProfile,
    onCloseMedicationProfile,
    actionBarGroup,
    themeStyle,
    onSubmit,
    form,
  } = usePatientProfile();

  const {
    showEditButton,
    showMhrSyncingButton,
    showReviewTriggerButton,
    showMedicationProfileButton,
    showReconciliationSummary,
  } = actionBarGroup;

  const canEditPatient =
    !selectedTenancy?.permissionsSetup ||
    (selectedTenancy?.permissionsSetup &&
      selectedTenancy?.userPermissions &&
      !!(selectedTenancy?.userPermissions)[SystemPermission.EDIT_PATIENT]);

  const ensureEpisodeTarget = (
    profile: typeof patientProfile,
    clinicIndex: number,
    targetEpisodeIndex: number,
  ) => {
    const clinic = profile?.clinics?.[clinicIndex];
    if (!clinic) {
      return null;
    }

    if (!Array.isArray(clinic.episodes)) {
      clinic.episodes = [];
    }

    if (!clinic.episodes[targetEpisodeIndex]) {
      clinic.episodes[targetEpisodeIndex] = {
        details: {},
      } as any;
    }

    const episode = clinic.episodes[targetEpisodeIndex] as any;
    if (!episode.details || typeof episode.details !== 'object') {
      episode.details = {};
    }

    return episode;
  };

  const handleInputChange = (e: any, index?: number, episodeIndex?: number) => {
    let temp = Object.assign({}, JSON.parse(JSON.stringify(patientProfile)));
    const clinicIndex = index ?? 0;
    const targetEpisodeIndex = episodeIndex ?? 0;
    if (Array.isArray(e.clinic_meta) && e.clinic_meta.length > 0) {
      const targetEpisode = ensureEpisodeTarget(
        temp,
        clinicIndex,
        targetEpisodeIndex,
      );
      if (!targetEpisode) return;
      const preValue = targetEpisode.details ?? {};
      targetEpisode.details = {
        ...preValue,
        episodeDetailsValues: e.clinic_meta,
      };
      dispatch(setPatientProfileState({ patientProfile: temp }));
    } else {
      const { name, value } = e.target;

      if (e?.isEpisodeDetail) {
        const targetEpisode = ensureEpisodeTarget(
          temp,
          clinicIndex,
          targetEpisodeIndex,
        );
        if (!targetEpisode) return;
        const currentDetails = targetEpisode.details ?? {};
        const detailKey = mapToEpisodeDetailsKey(e.detailKey ?? name);
        targetEpisode.details = {
          ...currentDetails,
          [detailKey]: value,
        };
        dispatch(setPatientProfileState({ patientProfile: temp }));
        return;
      }

      if (name.indexOf('episodeClosed') > -1) {
        const targetEpisode = ensureEpisodeTarget(
          temp,
          clinicIndex,
          targetEpisodeIndex,
        );
        if (!targetEpisode) return;
        targetEpisode.episode_closed = value === 'true';
        dispatch(setPatientProfileState({ patientProfile: temp }));
      } else if (name.indexOf('healthProfessionals') > -1) {
        const targetEpisode = ensureEpisodeTarget(
          temp,
          clinicIndex,
          targetEpisodeIndex,
        );
        if (!targetEpisode) return;
        const preValue = targetEpisode.details ?? {};
        targetEpisode.details = {
          ...preValue,
          healthProfessionals: value,
        };
        dispatch(setPatientProfileState({ patientProfile: temp }));
      } else {
        temp[name] = value;
        dispatch(setPatientProfileState({ patientProfile: temp }));
      }
    }
  };

  function toggleFavourite() {
    const temp = { ...patientProfile, favourite: !patientProfile.favourite };
    dispatch(checkingPatientProfileStatus());
    dispatch(setPatientProfileState({ patientProfile: temp }));
    if (patientAccessToken) {
      setFavourite(!temp.favourite, patientAccessToken);
    }
  }

  return !isLoading ? (
    <>
      <MedicationProfilesProvider>
        <Container fluid id="profileBar">
          {patientProfileStatus && (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!form.formState || !patientAccessToken) return;
                const formData = DataMapperProfileBar(
                  form.formState,
                  patientProfile,
                  patientAccessToken,
                  selectedTenancy,
                  [],
                );
                onSubmit(formData);
              }}
            >
              {hasDeidentifiedPermission ? (
                <DeIndentifiedPatientHeader
                  patientProfile={patientProfile}
                  toggleFavourite={toggleFavourite}
                />
              ) : (
                <>
                  <Row>
                    <Col lg="2">
                      <PatientInfo
                        profile={patientProfile}
                        toggleFavourite={toggleFavourite}
                      />
                      <TagsDropdown
                        dropdownOpen={dropdownOpen}
                        setDropdownOpen={setDropdownOpen}
                        availableTags={atags}
                        addTag={addTag}
                        patientProfile={patientProfile}
                        themeStyle={themeStyle}
                      />
                    </Col>
                    <Col lg="8" className="profilebar-details-col">
                      <PatientInfoDetails
                        collapsedConfig={config?.collapsedView}
                        expandedConfig={config?.expandedView}
                        patientContactConfig={config?.patientContactDetails}
                        freeTextConfig={config?.freeText}
                        clinics={clinics}
                        patientProfileStatus={patientProfileStatus}
                        handleInputChange={handleInputChange}
                      />
                    </Col>
                    <Col lg="2" className="hide-in-pdf">
                      <ExternalProviderButton
                        providers={[ExternalProvider.WITHINGS]}
                      />
                      <ActionButtons
                        setEditProfile={setEditProfile}
                        showEditButton={canEditPatient && showEditButton}
                        toggleMedicationProfile={toggleMedicationProfile}
                        onOpenAdditionalInsights={() =>
                          setOpenAdditionalInsights(true)
                        }
                        onOpenReconciliationSummary={() =>
                          setOpenReconciliationSummary(true)
                        }
                        showReconciliationSummary={showReconciliationSummary}
                        showReviewTriggerButton={showReviewTriggerButton}
                        showMedicationProfileButton={
                          showMedicationProfileButton
                        }
                        showMhrSyncingButton={showMhrSyncingButton}
                      />
                    </Col>
                  </Row>
                  <MoreDetails
                    onClickOpenPatientInfo={() => {
                      setOpenPatientInfo(!openPatientInfo);
                    }}
                    onClickEditProfile={() => {
                      setEditProfile(!editProfile);
                      form.reset({});
                    }}
                  />
                </>
              )}
            </Form>
          )}
        </Container>
        {openMedicationProfile && (
          <MedicationProfileCard onClose={onCloseMedicationProfile} />
        )}
        {patientProfileStatus && patientProfile?.id && newSectionConfig && (
          <AdditionalSection
            key={patientProfile.id}
            config={newSectionConfig}
            patientProfile={patientProfile}
            onInputChange={(arg) => {
              console.log({ arg });
            }}
          />
        )}
        <AdditionalInsightsPopup
          open={openAdditionalInsights}
          onHide={() => setOpenAdditionalInsights(false)}
        />
        <ReconciliationSummaryPopup
          open={openReconciliationSummary}
          onHide={() => setOpenReconciliationSummary(false)}
        />
      </MedicationProfilesProvider>
    </>
  ) : (
    <div className="d-flex justify-content-center align-items-center mt-5">
      <LoadingSpinner showText="Loading patient info ..." />
    </div>
  );
};

export { ProfileBar };
